import { post, get } from 'aws-amplify/api';

// Cache for browse customers to prevent multiple API calls
let browseCache: any[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCachedBrowseCustomers = async () => {
  const now = Date.now();

  // Return cached data if it's still fresh
  if (browseCache && now - cacheTimestamp < CACHE_DURATION) {
    console.log('Using cached browse customers');
    return browseCache;
  }

  // Fetch fresh data
  console.log('Fetching fresh browse customers');
  const { browseCustomers } = await import('./customerApi');
  browseCache = await browseCustomers();
  cacheTimestamp = now;

  return browseCache;
};

// Clear cache when needed
export const clearBrowseCache = () => {
  browseCache = null;
  cacheTimestamp = 0;
};

/**
 * Get the list of available customers
 * @returns The list of customer names
 */
export const getCustomersList = async (): Promise<string[]> => {
  try {
    // Use cached browse customers
    const customers = await getCachedBrowseCustomers();
    console.log('Raw browse customers response:', customers);

    // Extract customer names for the metrics dropdown
    const customerNames = customers.map((customer) => customer.name || customer.customerId);
    console.log('Extracted customer names:', customerNames);

    // Return only what the backend provides (no hardcoded ALL_CUSTOMERS)
    return customerNames;
  } catch (error) {
    console.error('Error getting customers list:', error);
    console.log('Customer list API failed - no fallback');
    return []; // Return empty array if API fails
  }
};

/**
 * Get metrics data for a specific customer
 * @param customerId The ID of the customer (or 'ALL_CUSTOMERS' for consolidated metrics)
 * @returns The metrics data for the customer
 */
export const getCustomerMetrics = async (customerId: string): Promise<any> => {
  try {
    let actualCustomerId = customerId;

    // Handle ALL_CUSTOMERS - find its actual customerId
    if (customerId === 'ALL_CUSTOMERS') {
      const userCustomers = await getCachedBrowseCustomers();
      const allCustomersRecord = userCustomers.find(
        (customer) => customer.name === 'ALL_CUSTOMERS' || customer.customerName === 'ALL_CUSTOMERS',
      );

      if (allCustomersRecord) {
        actualCustomerId = allCustomersRecord.customerId;
      } else {
        throw new Error('ALL_CUSTOMERS data not found');
      }
    } else {
      // Regular customer - validate user has access and get the actual customerId
      const userCustomers = await getCachedBrowseCustomers();
      console.log('Looking for customer:', customerId);
      console.log(
        'Available customers:',
        userCustomers.map((c) => ({ name: c.name, customerId: c.customerId })),
      );

      const customerRecord = userCustomers.find(
        (customer) => customer.name === customerId || customer.customerId === customerId,
      );

      if (!customerRecord) {
        throw new Error(`Access denied: User does not have access to customer "${customerId}"`);
      }

      // Use the customerId (secure ID like "customer1") for the API call
      actualCustomerId = customerRecord.customerId;
    }

    const { fetchAuthSession } = await import('aws-amplify/auth');
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    console.log(`Fetching metrics for customer: "${customerId}" (actual ID: "${actualCustomerId}")`);
    // URL encode the actual customer ID for the API
    const encodedCustomerId = encodeURIComponent(actualCustomerId);
    console.log(`Encoded customer ID: "${encodedCustomerId}"`);
    const response = await get({
      apiName: 'metricsApi',
      path: `/api/metrics/${encodedCustomerId}`,
      options: {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      },
    }).response;
    const data = (await response.body.json()) as any;
    console.log(`Successfully fetched metrics for ${customerId}:`, data);
    return data;
  } catch (error) {
    console.error(`Error getting metrics for customer ${customerId}:`, error);
    console.log(`API call failed for customer: "${customerId}"`);
    throw error;
  }
};

/**
 * Get consolidated metrics for all customers
 * @returns The consolidated metrics data
 */
export const getAllCustomersMetrics = async (): Promise<any> => {
  try {
    return await getCustomerMetrics('ALL_CUSTOMERS');
  } catch (error) {
    console.error('Error getting consolidated metrics:', error);
    throw error;
  }
};

/**
 * Generate insights from metrics data
 * @param metricsData The metrics data
 * @returns Array of insight strings
 */
export const generateInsights = (metricsData: any): string[] => {
  const insights: string[] = [];

  if (!metricsData || !metricsData.metrics) {
    return ['No metrics data available for insights'];
  }

  // Helper function to safely format numbers
  const safeToFixed = (value: any, digits: number = 1): string => {
    if (value === undefined || value === null) return '0';
    // Convert to number if it's not already
    const num = typeof value === 'number' ? value : Number(value);
    // Check if it's a valid number
    return isNaN(num) ? '0' : num.toFixed(digits);
  };

  const metrics = metricsData.metrics;

  // Active users insights
  const weeklyActive = metrics.weekly_active_users_growth || {};
  const recentWeeks = weeklyActive.recent_weeks_data || [];

  if (recentWeeks.length > 0) {
    const latestWeek = recentWeeks[recentWeeks.length - 1];
    const latestActive = latestWeek.active_users || 0;
    const latestGrowth = Number(latestWeek.growth_rate || 0);
    const avgGrowth = Number(weeklyActive.average_growth_rate || 0);

    insights.push(
      `Active Users: ${latestActive} IDE Pro users (${latestGrowth > 0 ? '+' : ''}${safeToFixed(latestGrowth)}% change)`,
    );
    insights.push(`Growth Momentum: ${safeToFixed(avgGrowth)}% average weekly growth over recent period`);

    // Trend analysis
    if (latestGrowth > 10) {
      insights.push('Strong Performance: Double-digit growth in latest week shows excellent momentum');
    } else if (latestGrowth > 0) {
      insights.push('Positive Trend: Continued growth in active user base');
    } else {
      insights.push('Attention Needed: Recent decline in active users requires investigation');
    }
  }

  // Paid users insights
  const weeklyPaid = metrics.weekly_paid_users_trend || {};
  const paidRecent = weeklyPaid.recent_weeks_data || [];

  if (paidRecent.length > 0) {
    const latestPaidWeek = paidRecent[paidRecent.length - 1];
    const latestPaid = latestPaidWeek.paid_users || 0;
    const paidGrowth = Number(latestPaidWeek.growth_rate || 0);
    const avgPaidGrowth = Number(weeklyPaid.average_growth_rate || 0);

    insights.push(
      `Revenue Growth: ${latestPaid} paid users with ${paidGrowth > 0 ? '+' : ''}${safeToFixed(paidGrowth)}% weekly change`,
    );
    insights.push(`Paid User Trend: ${safeToFixed(avgPaidGrowth)}% average growth in paid user base`);
  }

  // T4W insights
  const activeT4w = metrics.active_t4w_trend || {};
  const t4wCount = activeT4w.latest_t4w_count || 0;

  const paidT4w = metrics.paid_t4w_trend || {};
  const paidT4wCount = paidT4w.latest_t4w_count || 0;

  insights.push(`User Retention: ${t4wCount} users active in trailing 4 weeks`);
  insights.push(`Revenue Retention: ${paidT4wCount} total paid user interactions in T4W`);

  // Summary insights
  const summary = metrics.summary || {};
  const totalEntitled = Number(summary.total_entitled_users || 0);
  const totalPaid = Number(summary.total_paid_users || 0);
  const uniqueActive = Number(summary.unique_active_users || 0);

  if (totalEntitled > 0 && totalPaid > 0) {
    const conversionRate = (totalPaid / totalEntitled) * 100;
    insights.push(`Conversion Rate: ${safeToFixed(conversionRate)}% of entitled users are actively paying`);
  }

  if (uniqueActive > 0 && totalPaid > 0) {
    const engagementRatio = (uniqueActive / totalPaid) * 100;
    insights.push(`Engagement: ${safeToFixed(engagementRatio)}% of paid users are actively using IDE Pro`);
  }

  // Recommendations based on trends
  const avgGrowth = Number(weeklyActive.average_growth_rate || 0);

  if (avgGrowth > 5) {
    insights.push('Recommendation: Maintain current growth strategies - performance is strong');
  } else if (avgGrowth > 0) {
    insights.push('Recommendation: Identify growth accelerators to boost user acquisition');
  } else {
    insights.push('Action Required: Investigate factors causing user growth challenges');
  }

  return insights;
};
